import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider
} from '@mui/material';
import type { Person } from '../../../../types';

interface BiographyPanelProps {
  person: Person;
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

/**
 * BiographyPanel - Displays biographical information
 *
 * Shows:
 * - Biography text
 * - Personal notes
 * - Achievements
 * - Interests
 * - Personality traits
 * - Physical description
 */
export const BiographyPanel: React.FC<BiographyPanelProps> = ({
  person,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const hasBiography = person.biography;
  const hasPersonalNotes = person.personalNotes;
  const hasAchievements = person.achievements && person.achievements.length > 0;
  const hasInterests = person.interests && person.interests.length > 0;
  const hasPersonality = person.personality;
  const hasPhysicalDescription = person.physicalDescription;

  const hasAnyBiographicalInfo = hasBiography || hasPersonalNotes || hasAchievements ||
                                  hasInterests || hasPersonality || hasPhysicalDescription;

  if (!hasAnyBiographicalInfo) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
        No biographical information available
      </Typography>
    );
  }

  return (
    <Box>
      {/* Biography */}
      {hasBiography && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Biography
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            {person.biography}
          </Typography>
        </Box>
      )}

      {/* Personal Notes */}
      {hasPersonalNotes && (
        <Box sx={{ mb: 2 }}>
          {hasBiography && <Divider sx={{ mb: 2 }} />}
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Personal Notes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {person.personalNotes}
          </Typography>
        </Box>
      )}

      {/* Personality */}
      {hasPersonality && (
        <Box sx={{ mb: 2 }}>
          {(hasBiography || hasPersonalNotes) && <Divider sx={{ mb: 2 }} />}
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Personality
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            {person.personality}
          </Typography>
        </Box>
      )}

      {/* Physical Description */}
      {hasPhysicalDescription && (
        <Box sx={{ mb: 2 }}>
          {(hasBiography || hasPersonalNotes || hasPersonality) && <Divider sx={{ mb: 2 }} />}
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Physical Description
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            {person.physicalDescription}
          </Typography>
        </Box>
      )}

      {/* Interests */}
      {hasInterests && (
        <Box sx={{ mb: 2 }}>
          {(hasBiography || hasPersonalNotes || hasPersonality || hasPhysicalDescription) && <Divider sx={{ mb: 2 }} />}
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Interests
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {person.interests!.map((interest, index) => (
              <Chip
                key={index}
                label={interest}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Achievements */}
      {hasAchievements && (
        <Box>
          {(hasBiography || hasPersonalNotes || hasPersonality || hasPhysicalDescription || hasInterests) && <Divider sx={{ mb: 2 }} />}
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Achievements
          </Typography>
          <List dense>
            {person.achievements!.map((achievement, index) => (
              <ListItem key={index} sx={{ pl: 0 }}>
                <ListItemText secondary={`• ${achievement}`} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};
